import React, { useState, useEffect } from 'react';
import DynamicClock from './DynamicClock';

function ClockPage() {
  const [currentTime, setCurrentTime] = useState({ hours: 14, minutes: 30 });
  const [clockMode, setClockMode] = useState<'live' | 'manual' | 'both'>('both');
  const [clockFormat, setClockFormat] = useState<'12h' | '24h'>('24h');
  const [clockSize, setClockSize] = useState<'sm' | 'md' | 'lg'>('lg');
  const [showArabic, setShowArabic] = useState(true);
  const [showSpokenTime, setShowSpokenTime] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Live clock effect
  useEffect(() => {
    if (clockMode !== 'live') return;

    const updateFromSystem = () => {
      const now = new Date();
      setCurrentTime({
        hours: now.getHours(),
        minutes: now.getMinutes()
      });
    };

    updateFromSystem();
    const interval = setInterval(updateFromSystem, 1000);
    return () => clearInterval(interval);
  }, [clockMode]);

  const handleTimeChange = (hours: number, minutes: number) => {
    if (clockMode === 'manual') {
      setCurrentTime({ hours, minutes });
    } else if (clockMode === 'both') {
      setCurrentTime({ hours, minutes });
    }
  };

  const handleHourInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let hours = parseInt(e.target.value) || 0;
    if (clockFormat === '12h') {
      hours = hours % 12;
    } else {
      hours = Math.min(23, Math.max(0, hours));
    }
    setCurrentTime(prev => ({ ...prev, hours }));
  };

  const handleMinuteInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const minutes = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
    setCurrentTime(prev => ({ ...prev, minutes }));
  };

  const handleAMPMChange = (ampm: 'AM' | 'PM') => {
    let newHours = currentTime.hours % 12;
    if (ampm === 'PM') {
      newHours += 12;
    }
    setCurrentTime(prev => ({ ...prev, hours: newHours }));
  };

  const getDisplayHours = () => {
    if (clockFormat === '12h') {
      return currentTime.hours % 12 || 12;
    }
    return currentTime.hours;
  };

  const getAMPM = () => {
    return currentTime.hours >= 12 ? 'PM' : 'AM';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900">
      {/* Mobile Header with Toggle Button */}
      {/* <div className="lg:hidden fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md shadow-lg z-50 p-3 flex justify-between items-center">
        <h1 className="text-lg font-bold text-gray-800">🕐 My Learning Clock</h1>
        <button
          onClick={() => setShowControls(!showControls)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium text-sm shadow-lg"
        >
          {showControls ? '✕ Close Settings' : '⚙️ Settings'}
        </button>
      </div> */}

      <div className="flex flex-col lg:flex-row min-h-screen">
        
        {/* LEFT SIDE - CLOCK DISPLAY (Full height on desktop, top on mobile) */}
        <div className={`flex-1 flex items-center justify-center p-4 lg:p-8 transition-all duration-300 ${
          showControls && isMobile ? 'hidden' : 'flex'
        }`}>
          <div className="w-full h-full mt-0 lg:mt-0">
            <DynamicClock
              hours={currentTime.hours}
              minutes={currentTime.minutes}
              format={clockFormat}
              size={clockSize}
              showArabic={showArabic}
              showSpokenTime={showSpokenTime}
              onTimeChange={handleTimeChange}
            />
          </div>
        </div>

        {/* RIGHT SIDE - CONTROLS PANEL (Fixed width on desktop, bottom on mobile) */}
        <div className={`${
          showControls && isMobile 
            ? 'fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur-md rounded-t-2xl shadow-2xl max-h-[80vh] overflow-y-auto' 
            : 'w-full lg:w-96 bg-white shadow-2xl'
        } transition-all duration-300`}>
          <div className="p-4 lg:p-6 space-y-4 lg:space-y-6">
            {/* Header */}
            {(!showControls || !isMobile) && (
              <div className="text-center border-b border-gray-200 pb-4">
                <h2 className="text-xl lg:text-2xl font-bold text-gray-800">
                  ⚙️ Clock Settings
                </h2>
                <p className="text-xs lg:text-sm text-gray-500 mt-1">
                  Customize your display
                </p>
              </div>
            )}

            {/* Mode Selection */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                Mode
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(['live', 'manual', 'both'] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setClockMode(mode)}
                    className={`px-3 lg:px-4 py-2 rounded-lg font-medium transition-all text-sm lg:text-base ${
                      clockMode === mode
                        ? 'bg-purple-600 text-white shadow-lg transform scale-105'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {mode === 'live' && '🔴 Live'}
                    {mode === 'manual' && '✏️ Manual'}
                    {mode === 'both' && '🔄 Both'}
                  </button>
                ))}
              </div>
            </div>

            {/* Manual Time Input - Only show in manual or both mode */}
            {(clockMode === 'manual' || clockMode === 'both') && (
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Manual Time Control
                </label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-gray-500">Hours</label>
                    <input
                      type="number"
                      value={getDisplayHours()}
                      onChange={handleHourInput}
                      min={clockFormat === '12h' ? 1 : 0}
                      max={clockFormat === '12h' ? 12 : 23}
                      className="w-full px-3 py-2 text-center font-mono text-lg border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-gray-500">Minutes</label>
                    <input
                      type="number"
                      value={currentTime.minutes}
                      onChange={handleMinuteInput}
                      min={0}
                      max={59}
                      className="w-full px-3 py-2 text-center font-mono text-lg border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  {clockFormat === '12h' && (
                    <div className="flex-1">
                      <label className="text-xs text-gray-500">AM/PM</label>
                      <select
                        value={getAMPM()}
                        onChange={(e) => handleAMPMChange(e.target.value as 'AM' | 'PM')}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none bg-white"
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Format Selection */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                Time Format
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(['12h', '24h'] as const).map((format) => (
                  <button
                    key={format}
                    onClick={() => setClockFormat(format)}
                    className={`px-3 lg:px-4 py-2 rounded-lg font-medium transition-all text-sm lg:text-base ${
                      clockFormat === format
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {format === '12h' ? '12-Hour' : '24-Hour'}
                  </button>
                ))}
              </div>
            </div>

            {/* Size Selection */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                Clock Size
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(['sm', 'md', 'lg'] as const).map((size) => (
                  <button
                    key={size}
                    onClick={() => setClockSize(size)}
                    className={`px-3 lg:px-4 py-2 rounded-lg font-medium transition-all text-sm lg:text-base ${
                      clockSize === size
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {size === 'sm' && 'Small'}
                    {size === 'md' && 'Medium'}
                    {size === 'lg' && 'Large'}
                  </button>
                ))}
              </div>
            </div>

            {/* Toggle Options */}
            <div className="space-y-3">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                Display Options
              </label>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-800 text-sm lg:text-base">🕌 Arabic Time</div>
                  <div className="text-xs text-gray-500">Show digital time in Arabic</div>
                </div>
                <button
                  onClick={() => setShowArabic(!showArabic)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    showArabic ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showArabic ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-800 text-sm lg:text-base">🗣️ Spoken Time</div>
                  <div className="text-xs text-gray-500">Show natural language time</div>
                </div>
                <button
                  onClick={() => setShowSpokenTime(!showSpokenTime)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    showSpokenTime ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showSpokenTime ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Current Time Display */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-3 lg:p-4 border border-purple-200">
              <div className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-2">
                Current Selection
              </div>
              <div className="text-xl lg:text-2xl font-mono font-bold text-gray-800 text-center">
                {currentTime.hours.toString().padStart(2, '0')}:
                {currentTime.minutes.toString().padStart(2, '0')}
              </div>
              <div className="text-xs text-gray-500 text-center mt-2">
                {clockMode === 'live' && '🔴 Live mode active - auto updating'}
                {clockMode === 'manual' && '✏️ Manual mode - using manual controls'}
                {clockMode === 'both' && '🔄 Both modes - manual controls available'}
              </div>
            </div>

            {/* Reset Button */}
            <button
              onClick={() => {
                setClockMode('both');
                setClockFormat('24h');
                setClockSize('lg');
                setShowArabic(true);
                setShowSpokenTime(true);
                const now = new Date();
                setCurrentTime({ hours: now.getHours(), minutes: now.getMinutes() });
              }}
              className="w-full py-3 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-800 transition-all transform hover:scale-105 text-sm lg:text-base"
            >
              🔄 Reset to Default
            </button>

            {/* Close button for mobile */}
            {showControls && isMobile && (
              <button
                onClick={() => setShowControls(false)}
                className="w-full py-3 bg-purple-600 text-white rounded-lg font-medium text-sm"
              >
                ✓ Apply & Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClockPage;